(ns yardwerkz.web.auth
  "Clerk web authentication integration.
   Provides ClerkProvider wrapper, sign-in/sign-up components,
   and token provider setup for Capra RPC."
  (:require
   ["@clerk/clerk-react" :refer [ClerkProvider SignIn SignUp
                                 useAuth useUser useClerk]]
   [uix.core :refer [defui $ use-effect use-callback]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.effects :as effects]
   [yardwerkz.capra-client :as capra-client]
   [yardwerkz.capra-dispatch :as capra-dispatch]
   [yardwerkz.web.config :as config]
   [lambdaisland.glogi :as log]))

;; =============================================================================
;; Hooks
;; =============================================================================

(defn use-auth-hook
  "Wrapper around Clerk's useAuth for ClojureScript.
   Returns {:keys [is-signed-in is-loaded get-token user-id]}."
  []
  (let [auth (useAuth)]
    {:is-signed-in (.-isSignedIn auth)
     :is-loaded (.-isLoaded auth)
     :get-token (.-getToken auth)
     :user-id (.-userId auth)}))

(defn use-user-hook
  "Wrapper around Clerk's useUser.
   Returns {:keys [user is-loaded]}."
  []
  (let [u (useUser)]
    {:user (.-user u)
     :is-loaded (.-isLoaded u)}))

;; =============================================================================
;; Token Provider Setup
;; =============================================================================

(defui token-provider-setup
  "Invisible component that sets up the Capra token provider from Clerk's getToken.
   Must be rendered inside ClerkProvider."
  []
  (let [{:keys [get-token is-signed-in is-loaded]} (use-auth-hook)
        {:keys [user]} (use-user-hook)
        dispatch (rfx/use-dispatch)]

    ;; Set up token provider for Capra effects
    (use-effect
     (fn []
       (when (and is-loaded is-signed-in get-token)
         (effects/set-token-provider! (fn [] (get-token)))
         (log/info :auth/token-provider-set {}))
       js/undefined)
     [is-loaded is-signed-in get-token])

    ;; Sync identity to store when user is available
    (use-effect
     (fn []
       (when (and is-loaded is-signed-in user)
         (let [^js u user
               identity {:id (.-id u)
                         :email (some-> (.-primaryEmailAddress u) .-emailAddress)
                         :phone (some-> (.-primaryPhoneNumber u) .-phoneNumber)
                         :first-name (.-firstName u)
                         :last-name (.-lastName u)
                         :image-url (.-imageUrl u)}]
           (log/info :auth/identity-synced {:id (:id identity)})
           (dispatch [::store/set-identity identity])))
       js/undefined)
     [is-loaded is-signed-in user dispatch])

    ;; Clear identity on sign out
    (use-effect
     (fn []
       (when (and is-loaded (not is-signed-in))
         (capra-dispatch/reset-session!)
         (dispatch [::store/clear-identity]))
       js/undefined)
     [is-loaded is-signed-in dispatch])

    nil))

;; =============================================================================
;; Capra Connection Manager
;; =============================================================================

(defui capra-connection-manager
  "Establishes Capra RPC connection when user is authenticated.
   Must be rendered inside ClerkProvider."
  []
  (let [{:keys [is-signed-in is-loaded]} (use-auth-hook)]
    (use-effect
     (fn []
       (when (and is-loaded is-signed-in)
         (capra-client/init! config/API_URL)
         (capra-dispatch/set-active-role! :customer)
         (log/info :auth/capra-initialized {:url config/API_URL}))
       js/undefined)
     [is-loaded is-signed-in])
    nil))

;; =============================================================================
;; Auth Gate
;; =============================================================================

(defui auth-gate
  "Shows sign-in page when not authenticated, renders children when authenticated."
  [{:keys [children]}]
  (let [{:keys [is-signed-in is-loaded]} (use-auth-hook)]
    (cond
      (not is-loaded)
      ($ :div {:class "flex items-center justify-center min-h-screen"}
         ($ :div {:class "animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"}))

      (not is-signed-in)
      ($ :div {:class "flex items-center justify-center min-h-screen bg-gray-50"}
         ($ :div {:class "w-full max-w-md"}
            ($ SignIn {:routing "hash"
                       :appearance #js {:elements #js {:rootBox "mx-auto"}}})))

      :else
      ($ :<>
         ($ token-provider-setup)
         ($ capra-connection-manager)
         children))))

;; =============================================================================
;; Provider
;; =============================================================================

(defui clerk-provider
  "Wraps the app in ClerkProvider with the publishable key."
  [{:keys [children]}]
  ($ ClerkProvider {:publishableKey config/CLERK_KEY}
     children))
